import React, { useState } from 'react';
import { apiService } from '../../services/apiService';
import { emailTemplates, getTemplateById } from './emailTemplates';

interface EmailFormData {
  to: string;
  subject: string;
  body: string;
  isHtml: boolean;
}

interface CsvRecipient {
  email: string;
  name?: string;
}

// interface BulkEmailData {
//   recipients: CsvRecipient[];
//   subject: string;
//   body: string;
//   isHtml: boolean;
// }

const Email: React.FC = () => {
  // Load default template (TestFlight reminder)
  const defaultTemplate = emailTemplates[0];
  
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>(defaultTemplate.id);
  const [formData, setFormData] = useState<EmailFormData>({
    to: '',
    subject: defaultTemplate.subject,
    body: defaultTemplate.body,
    isHtml: defaultTemplate.isHtml
  });
  
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [csvRecipients, setCsvRecipients] = useState<CsvRecipient[]>([]);
  const [isBulkMode, setIsBulkMode] = useState(false);
  const [bulkProgress, setBulkProgress] = useState<{ current: number; total: number } | null>(null);
  const [emailIds, setEmailIds] = useState<string[]>([]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle template selection
  const handleTemplateSelect = (templateId: string) => {
    const template = getTemplateById(templateId);
    if (template) {
      setSelectedTemplateId(templateId);
      setFormData(prev => ({
        ...prev,
        subject: template.subject,
        body: template.body,
        isHtml: template.isHtml
      }));
      setMessage({ type: 'success', text: `Template "${template.name}" loaded successfully!` });
    }
  };


  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.to || !formData.subject || !formData.body) {
      setMessage({ type: 'error', text: 'Please fill in all fields' });
      return;
    }

    setIsLoading(true);
    setMessage(null);

    try {
      const result = await apiService.sendEmail(formData);
      
      if (result.success) {
        const ids = result.emailIds || [];
        setEmailIds(ids);
        setMessage({ type: 'success', text: `Email sent successfully! ID: ${ids[0] || 'N/A'}` });
        console.log('Email sent successfully. IDs:', ids);
        setFormData({ to: '', subject: '', body: '', isHtml: false });
      } else {
        setMessage({ type: 'error', text: result.error || 'Failed to send email' });
      }
    } catch (error) {
      setMessage({ 
        type: 'error', 
        text: error instanceof Error ? error.message : 'Failed to send email' 
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleClear = () => {
    // Reset to default template
    const defaultTemplate = emailTemplates[0];
    setSelectedTemplateId(defaultTemplate.id);
    setFormData({ 
      to: '', 
      subject: defaultTemplate.subject, 
      body: defaultTemplate.body, 
      isHtml: defaultTemplate.isHtml 
    });
    setMessage(null);
    setCsvRecipients([]);
    setIsBulkMode(false);
    setBulkProgress(null);
    setEmailIds([]);
  };

  const handleDownloadEmailIds = () => {
    if (emailIds.length === 0) {
      setMessage({ type: 'error', text: 'No email IDs to download' });
      return;
    }

    const data = {
      timestamp: new Date().toISOString(),
      totalEmails: emailIds.length,
      emailIds: emailIds,
      recipients: csvRecipients.length > 0 ? csvRecipients.map(r => ({ email: r.email, name: r.name })) : [{ email: formData.to, name: 'Single recipient' }]
    };

    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `email-ids-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleCsvUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const csvText = event.target?.result as string;
        const lines = csvText.split('\n').filter(line => line.trim());
        
        if (lines.length === 0) {
          setMessage({ type: 'error', text: 'CSV file is empty' });
          return;
        }

        const recipients: CsvRecipient[] = [];
        
        for (let i = 0; i < lines.length; i++) {
          const line = lines[i].trim();
          if (!line) continue;

          // Parse CSV line (handle quoted fields)
          const fields = line.split(',').map(field => field.trim().replace(/^"|"$/g, ''));
          
          if (fields.length >= 1 && fields[0]) {
            recipients.push({
              email: fields[0],
              name: fields[1] || undefined
            });
          }
        }

        if (recipients.length === 0) {
          setMessage({ type: 'error', text: 'No valid email addresses found in CSV' });
          return;
        }

        setCsvRecipients(recipients);
        setIsBulkMode(true);
        setMessage({ type: 'success', text: `Imported ${recipients.length} recipients from CSV` });
      } catch (error) {
        setMessage({ type: 'error', text: 'Error parsing CSV file' });
      }
    };

    reader.readAsText(file);
  };

  const handleBulkSend = async () => {
    if (csvRecipients.length === 0) {
      setMessage({ type: 'error', text: 'No recipients to send to' });
      return;
    }

    if (!formData.subject || !formData.body) {
      setMessage({ type: 'error', text: 'Please fill in subject and body' });
      return;
    }

    setIsLoading(true);
    setBulkProgress({ current: 0, total: csvRecipients.length });
    setMessage(null);

    try {
      // Prepare batch email data
      const batchEmails = csvRecipients.map(recipient => ({
        to: recipient.email,
        subject: formData.subject,
        body: formData.body,
        isHtml: formData.isHtml
      }));

      setBulkProgress({ current: csvRecipients.length, total: csvRecipients.length });

      // Send batch emails
      const result = await apiService.sendBatchEmails(batchEmails);

      if (result.success) {
        const ids = result.emailIds || [];
        setEmailIds(ids);
        setMessage({ type: 'success', text: `Successfully sent ${csvRecipients.length} emails!` });
        console.log('Batch emails sent successfully. IDs:', ids);
      } else {
        setMessage({ type: 'error', text: result.error || 'Failed to send batch emails' });
      }
    } catch (error) {
      setMessage({ 
        type: 'error', 
        text: error instanceof Error ? error.message : 'Failed to send batch emails' 
      });
    } finally {
      setBulkProgress(null);
      setIsLoading(false);
    }
  };

  return (
    <div className="email-container">
      <div className="card">
        <div className="card-header">
          <h3>📧 Send Email</h3>
          <p>Send emails using the Resend API</p>
        </div>

        <form onSubmit={handleSubmit} className="email-form">
          {/* Email Template Selector */}
          <div className="form-group">
            <label className="form-label">📝 Email Template Bank</label>
            <div className="template-selector">
              {emailTemplates.map((template) => (
                <button
                  key={template.id}
                  type="button"
                  className={`template-card ${selectedTemplateId === template.id ? 'active' : ''}`}
                  onClick={() => handleTemplateSelect(template.id)}
                  disabled={isLoading}
                >
                  <div className="template-card-header">
                    <span className="template-icon">
                      {template.category === 'testflight' ? '✈️' : 
                       template.category === 'launch' ? '🚀' : '📧'}
                    </span>
                    <span className="template-name">{template.name}</span>
                  </div>
                  <p className="template-description">{template.description}</p>
                  <span className={`template-badge ${template.category}`}>
                    {template.category}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* CSV Import Section */}
          <div className="form-group">
            <label className="form-label">Import Recipients from CSV</label>
            <div className="csv-import-section">
              <input
                type="file"
                id="csvFile"
                accept=".csv"
                onChange={handleCsvUpload}
                className="csv-file-input"
                disabled={isLoading}
              />
              <label htmlFor="csvFile" className="csv-file-label">
                📁 Choose CSV File
              </label>
              <small className="csv-help-text">
                CSV format: email,name (optional). Example: john@example.com,John Doe
              </small>
            </div>
          </div>

          {/* Recipients Display */}
          {csvRecipients.length > 0 && (
            <div className="form-group">
              <label className="form-label">
                Recipients ({csvRecipients.length})
                <button
                  type="button"
                  onClick={() => {
                    setCsvRecipients([]);
                    setIsBulkMode(false);
                  }}
                  className="btn btn-secondary btn-sm"
                  disabled={isLoading}
                >
                  Clear
                </button>
              </label>
              <div className="recipients-list">
                {csvRecipients.slice(0, 5).map((recipient, index) => (
                  <div key={index} className="recipient-item">
                    <span className="recipient-email">{recipient.email}</span>
                    {recipient.name && <span className="recipient-name">({recipient.name})</span>}
                  </div>
                ))}
                {csvRecipients.length > 5 && (
                  <div className="recipient-more">
                    ... and {csvRecipients.length - 5} more
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Single Email Input (only show if not in bulk mode) */}
          {!isBulkMode && (
            <div className="form-group">
              <label htmlFor="to" className="form-label">
                To Email Address <span className="required">*</span>
              </label>
              <input
                type="email"
                id="to"
                name="to"
                value={formData.to}
                onChange={handleInputChange}
                className="form-input"
                placeholder="recipient@example.com"
                required
                disabled={isLoading}
              />
            </div>
          )}

          <div className="form-group">
            <label htmlFor="subject" className="form-label">
              Subject <span className="required">*</span>
            </label>
            <input
              type="text"
              id="subject"
              name="subject"
              value={formData.subject}
              onChange={handleInputChange}
              className="form-input"
              placeholder="Email subject"
              required
              disabled={isLoading}
            />
          </div>

          <div className="form-group">
            <label className="form-label">Email Format</label>
            <div className="format-toggle-container">
              <button
                type="button"
                className={`format-toggle-btn ${!formData.isHtml ? 'active' : ''}`}
                onClick={() => setFormData(prev => ({ ...prev, isHtml: false }))}
                disabled={isLoading}
              >
                📝 Plain Text
              </button>
              <button
                type="button"
                className={`format-toggle-btn ${formData.isHtml ? 'active' : ''}`}
                onClick={() => setFormData(prev => ({ ...prev, isHtml: true }))}
                disabled={isLoading}
              >
                🌐 HTML
              </button>
            </div>
            <small className="format-description">
              {formData.isHtml 
                ? 'HTML formatting will be preserved in the email' 
                : 'Plain text will be converted to HTML automatically'
              }
            </small>
          </div>

          <div className="form-group">
            <label htmlFor="body" className="form-label">
              Message Body <span className="required">*</span>
              {formData.isHtml && (
                <span className="html-indicator">(HTML Mode)</span>
              )}
            </label>
            <textarea
              id="body"
              name="body"
              value={formData.body}
              onChange={handleInputChange}
              className="form-textarea"
              placeholder={formData.isHtml 
                ? `Enter your HTML message here...\n\nExample:\n<h1>Hello!</h1>\n<p>This is a <strong>bold</strong> message.</p>\n<a href="https://example.com">Click here</a>`
                : "Enter your message here..."
              }
              rows={12}
              required
              disabled={isLoading}
            />
            {formData.isHtml && (
              <div className="html-preview">
                <h4>HTML Preview:</h4>
                <div 
                  className="preview-content"
                  dangerouslySetInnerHTML={{ __html: formData.body || '<em>Enter HTML content to see preview...</em>' }}
                />
              </div>
            )}
          </div>

          {message && (
            <div className={`message ${message.type}`}>
              {message.type === 'success' ? '✅' : '❌'} {message.text}
            </div>
          )}

          {/* Bulk Progress */}
          {bulkProgress && (
            <div className="bulk-progress">
              <div className="progress-bar">
                <div 
                  className="progress-fill" 
                  style={{ width: `${(bulkProgress.current / bulkProgress.total) * 100}%` }}
                ></div>
              </div>
              <div className="progress-text">
                Sending {bulkProgress.current} of {bulkProgress.total} emails...
              </div>
            </div>
          )}

          <div className="form-actions">
            <button
              type="button"
              onClick={handleClear}
              className="btn btn-secondary"
              disabled={isLoading}
            >
              🗑️ Clear
            </button>
            {emailIds.length > 0 && (
              <button
                type="button"
                onClick={handleDownloadEmailIds}
                className="btn btn-secondary"
                disabled={isLoading}
              >
                📥 Download Email IDs ({emailIds.length})
              </button>
            )}
            {isBulkMode ? (
              <button
                type="button"
                onClick={handleBulkSend}
                className="btn btn-primary"
                disabled={isLoading || csvRecipients.length === 0}
              >
                {isLoading ? '⏳ Sending...' : `📧 Send to ${csvRecipients.length} Recipients`}
              </button>
            ) : (
              <button
                type="submit"
                className="btn btn-primary"
                disabled={isLoading}
              >
                {isLoading ? '⏳ Sending...' : '📧 Send Email'}
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default Email;
